import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, Upload, CheckCircle2, Clock, XCircle, Ban } from "lucide-react";

const ROLE_LABEL = { influencer: "Influencer", sub_admin: "Worker", partner: "Partner" };

export function VerificationGate({ children }) {
  const [state, setState] = useState(null);
  const load = () => api.get("/verification/me").then(({ data }) => setState(data)).catch(() => setState(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (state === null) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>;
  if (state === false) return children;
  if (state.verification.status === "verified") return children;
  return <div className="max-w-2xl mx-auto"><VerificationCard state={state} onDone={load} /></div>;
}

function VerificationCard({ state, onDone }) {
  const v = state.verification;
  const contract = state.contract;
  const roleLabel = ROLE_LABEL[state.role] || "Account";
  const [name, setName] = useState(v.full_legal_name || "");
  const [idnum, setIdnum] = useState(v.id_number || "");
  const [signature, setSignature] = useState(v.signature_name || "");
  const [agreed, setAgreed] = useState(false);
  const [uploaded, setUploaded] = useState(v.id_document_uploaded);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (v.status === "under_review") {
    return (
      <div className="bg-white border border-blue-200 rounded-xl shadow-sm p-8 text-center mt-4" data-testid="verify-under-review">
        <Clock className="w-10 h-10 text-blue-500 mx-auto" />
        <h2 className="font-display text-2xl font-bold text-[#0B2447] mt-4">Verification under review</h2>
        <p className="text-slate-600 mt-2">Thanks {v.full_legal_name || "there"} — we've received your details and signed contract. An administrator will review and activate your account shortly. You'll be able to operate once approved.</p>
      </div>
    );
  }
  if (v.status === "suspended") {
    return (
      <div className="bg-white border border-red-200 rounded-xl shadow-sm p-8 text-center mt-4" data-testid="verify-suspended">
        <Ban className="w-10 h-10 text-red-500 mx-auto" />
        <h2 className="font-display text-2xl font-bold text-[#0B2447] mt-4">Account suspended</h2>
        <p className="text-slate-600 mt-2">Your account has been suspended{v.reject_reason ? `: ${v.reject_reason}` : "."} Please contact support for assistance.</p>
      </div>
    );
  }

  const uploadId = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/verification/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(true);
      toast.success("ID document uploaded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed."); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Enter your full legal name."); return; }
    if (!/^\d{13}$/.test(idnum.trim())) { toast.error("Enter your 13-digit SA ID number."); return; }
    if (!uploaded) { toast.error("Upload your ID document first."); return; }
    if (!agreed) { toast.error("You must confirm you've read and agree to the contract."); return; }
    if (!signature.trim()) { toast.error("Type your full name to sign."); return; }
    setBusy(true);
    try {
      await api.post("/verification/submit", { full_legal_name: name, id_number: idnum, signature_name: signature, contract_version: contract.version, agreed: true });
      toast.success("Submitted for verification!");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 md:p-8 mt-4" data-testid="verify-form">
      <div className="flex items-center gap-2 text-[#0B2447]"><ShieldCheck className="w-5 h-5 text-[#F5C32C]" /><span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{roleLabel} verification</span></div>
      <h2 className="font-display text-2xl font-bold text-[#0B2447] mt-2">Verify your account to get started</h2>
      <p className="text-slate-600 mt-1">For security and compliance (POPIA), we need to confirm your identity and have you sign your agreement before your account is activated.</p>
      {v.status === "rejected" && v.reject_reason && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="verify-reject-reason"><XCircle className="w-4 h-4 inline mr-1.5" />Your previous submission was rejected: {v.reject_reason}. Please correct and resubmit.</div>
      )}

      <div className="mt-6 space-y-4">
        <div><Label>Full legal name (as on your ID)</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="verify-fullname" placeholder="e.g. Thabo James Mokoena" /></div>
        <div><Label>South African ID number</Label><Input value={idnum} onChange={(e) => setIdnum(e.target.value.replace(/\D/g, "").slice(0, 13))} data-testid="verify-idnumber" placeholder="13 digits" inputMode="numeric" /></div>
        <div>
          <Label>ID document (PDF/JPG/PNG, max 10MB)</Label>
          <label className="mt-1.5 flex items-center gap-2 cursor-pointer">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="verify-id-upload" onChange={(e) => uploadId(e.target.files?.[0])} />
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-[#0B2447]/30 text-[#0B2447] hover:bg-slate-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : uploaded ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Upload className="w-4 h-4" />}
              {uploaded ? "Replace document" : "Upload ID document"}
            </span>
            {uploaded && <span className="text-xs text-emerald-600" data-testid="verify-id-uploaded">✓ Uploaded</span>}
          </label>
        </div>
      </div>

      <div className="mt-6">
        <Label>{contract.title}</Label>
        <div className="mt-1.5 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-line" data-testid="verify-contract-text">{contract.body}</div>
        <p className="text-xs text-slate-400 mt-1">Contract version {contract.version}</p>
      </div>

      <label className="mt-4 flex items-start gap-3 cursor-pointer">
        <Checkbox checked={agreed} onCheckedChange={(x) => setAgreed(!!x)} data-testid="verify-agree" className="mt-0.5" />
        <span className="text-sm text-slate-600">I have read and understood this agreement and I agree to be bound by its terms.</span>
      </label>
      <div className="mt-4"><Label>Electronic signature — type your full name</Label><Input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="verify-signature" placeholder="Type your full name to sign" /></div>

      <Button onClick={submit} disabled={busy} data-testid="verify-submit" className="mt-6 w-full bg-[#0B2447] hover:bg-[#153461] py-6 text-base">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for verification"}
      </Button>
    </div>
  );
}
